import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { formatDuration } from 'utils/Helpers';
import {
  Text,
  Button,
  FlatButton,
  ResultCompact,
  OutlineButton,
  Spacing,
  Icon,
} from '@reservamos/elements';
import TimeIcon from 'ui/atoms/TimeIcon';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import useUrgencyDiscountUIEnabled from 'hooks/useUrgencyDiscountUIEnabled';
import { tripHasAnniversaryIcon } from 'utils/specialDiscount';
import useWhitelabelTheme from 'hooks/whitelabel/useWhitelabelTheme';
import useQuickPurchaseCTAExperiment from 'hooks/useQuickPurchaseCTAExperiment';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import useSearch from 'hooks/store/useSearch';
import '../../ui/layouts/MatrixResult';
import PreviousPrice from 'ui/atoms/PreviousPrice';
import TripSchedule from 'ui/atoms/TripSchedule';
import getRouteLabel from 'utils/trips/getRouteLabel';
import getClosedTripButtonText from 'utils/trips/getClosedTripButtonText';
import DotersFlatfareBadge from 'ui/atoms/DotersFlatfareBadge';
import { hasUrgencyDiscountAmount, isResultDiscountUIType } from 'utils/urgencyDiscount';
import TripFiltersBadges from './TripFiltersBadges';
import NewPriceComponent from './NewPriceComponent';
import TripDiscountAdvice from './TripDiscountAdvice';
import RouteDetailsModal from '../../ui/molecules/RouteDetailsModal';
import ResultCategoriesAvailability from './ResultCategoriesAvailability';
import quickPurchaseIconSrc from '../../images/purchases/quick-purchase.png';
import WalletPrice from './WalletPrice';

/**
 * This result is used by:
 * - GFA (Primera Plus, Roll & Bits), Conecta GFA)
 * - Rápido Ochoa
 * - Berlinas del Fonce
 * - Cruz del Sur
 */

// hide the duration if is minor than 16min
// because this is probably wrong estimation
const MIN_DURATION = 15;

/**
 * Component for displaying a discount advice. Could be a discount badge or a flat fare badge.
 * @param {Object} params - The component props.
 * @param {boolean} params.showDiscount - Flag indicating if the discount should be shown.
 * @param {boolean} params.showFlatFare - Flag indicating if the flat fare should be shown.
 * @param {string} params.discountType - The type of discount.
 * @param {number} params.discountAvailability - The availability of the discount.
 * @param {number} params.discountAmount - The discount amount.
 * @param {number} [params.discountPercent] - The discount percentage.
 * @param {number} params.total - The total price of the trip.
 * @param {string} params.way - The direction of the trip.
 * @returns {JSX.Element | null} The rendered component.
 */
const DiscountAdvice = ({
  showDiscount,
  showFlatFare,
  discountType,
  discountAvailability,
  discountAmount,
  discountPercent,
  total,
  way,
}) => {
  if (!showDiscount) return null;

  if (showFlatFare) {
    return <DotersFlatfareBadge />;
  }
  return (
    <TripDiscountAdvice
      discountType={discountType}
      discountAvailability={discountAvailability}
      discountAmount={discountAmount}
      discountPercent={discountPercent}
      tripTotal={total}
      way={way}
    />
  );
};

DiscountAdvice.propTypes = {
  showDiscount: PropTypes.bool,
  showFlatFare: PropTypes.bool,
  discountType: PropTypes.string,
  discountAvailability: PropTypes.number,
  discountAmount: PropTypes.number,
  discountPercent: PropTypes.number,
  total: PropTypes.number,
  way: PropTypes.string,
};

/**
 * Component for displaying a new result in GFA format.
 *
 * @param {Object} props - The component props.
 * @param {String} props.id - Trip Id.
 * @param {Date} props.arrival - The arrival date.
 * @param {Date} props.departure - The departure date.
 * @param {string} props.destination - The destination of the trip.
 * @param {number} props.duration - The duration of the trip.
 * @param {function} props.onSelectClick - The function to handle the selection of the trip.
 * @param {Object} props.providerDiscount - The discount information for the provider.
 * @param {number} props.total - The total price of the trip.
 * @param {Object} props.trip - The trip details.
 * @param {boolean} props.highLightType - Flag indicating if the result should be highlighted.
 * @param {string} props.highlightText - The text to highlight.
 * @param {string} props.sparkleBadgeLabel - Text for the highlighted sparkle badge.
 * @param {boolean} props.isExchange - Flag indicating if it's an exchange trip.
 * @param {string} props.way - The direction of the trip.
 * @param {Array} props.filtersApplied - The filters applied to the trip.
 * @param {boolean} props.showWalletPoints - Flag indicating if wallet points should be shown.
 * @param {number} props.walletPoints - The wallet points available.
 * @param {boolean} props.useFlatFare - Flag indicating if flat fare should be shown.
 * @param {Object} props.pricing - The pricing information for the trip.
 * @param {string} props.walletType - The type of wallet.
 * @param {string} props.serviceType - The type of service.
 * @param {boolean} props.highLightAsMobile - Flag indicating if the result should be highlighted as mobile.
 * @param {Array} props.availableCategories - The available categories for the trip.
 * @param {boolean} props.supportWoman - Flag indicating if
 * @param {boolean} props.showNewFiltersList - Flag indicating if the new filters list should be shown.
 * @param {number} props.totalBeforeDiscount - The total price of the trip before discount.
 * @param {number} props.availability - The number of available seats for the trip.
 * @param {number} [props.officePrice] - The office (window) price from the provider.
 * @param {node} props.children - The children to render.
 * @param {boolean} props.isOpen - Flag indicating if the result is open.
 * @param {boolean} props.recentView - Flag indicating if the result is in the recent view.
 * @param {boolean} props.isSeatsOnResults - Flag indicating if the seats should be shown on the results.
 * @param {string} props.origin - The origin of the trip.
 * @param {boolean} props.shouldShowProviderDiscount - Flag indicating if the provider discount advice should be shown.
 * @param {boolean} props.isRecommendedList - Flag indicating if the result is inside the recommended trips list.
 * @param {boolean} props.showQuickPurchase - Flag indicating if the quick purchase button should be shown.
 * @param {Function} props.onQuickPurchaseClick - The function to handle the quick purchase action.
 * @param {boolean} props.isClosed - Flag indicating if the trip is closed (sold out or departed).
 */
const SimpleTripResult = ({
  id,
  arrival,
  departure,
  duration,
  onSelectClick,
  providerDiscount,
  total,
  trip,
  highLightType,
  highlightText,
  sparkleBadgeLabel,
  isExchange,
  way,
  filtersApplied,
  showWalletPoints,
  walletPoints,
  useFlatFare,
  pricing,
  walletType,
  serviceType,
  highLightAsMobile,
  availableCategories,
  supportWoman,
  showNewFiltersList,
  totalBeforeDiscount,
  officePrice,
  children,
  isOpen,
  recentView,
  destination,
  origin,
  isSeatsOnResults,
  availability,
  shouldShowProviderDiscount,
  showQuickPurchase,
  onQuickPurchaseClick,
  isRecommendedList,
  isClosed,
}) => {
  const [showDetailsModal, setShowDetailsModal] = useState(true);
  const environment = useWhitelabelEnvs();
  const features = useWhitelabelFeatures();
  const { getServiceType, serviceTypes, resultsCTAColor } = useWhitelabelTheme();
  const { t } = useTranslation();
  const quickPurchaseCTACopy = useQuickPurchaseCTAExperiment();
  const showEarlyDiscounts = features.SHOW_EARLY_DISCOUNTS;
  const { line, variableDepartureTime, stops, service, seatsRecline, comingFrom, path } = trip;
  const showDuration = duration > MIN_DURATION;
  const lowAvailability = availability <= features.LOW_AVAILABILITY_THRESHOLD;

  const { departure: searchDeparture } = useSearch();

  // Handles logic for displaying provider logos in case of numbered travels for ConectaGFA
  const buckTicketEnabled = features.BULK_TICKET_ENABLED;
  const { routeType } = searchDeparture;
  const isBulkTicket = routeType === 'bulk_ticket';
  const showProviderLogosForNumberedTickets = buckTicketEnabled && !isBulkTicket;

  /**
   * Determines the result layout based on ticket type and configuration.
   * @returns {boolean} True to show provider logos layout, false for original layout.
   */
  const getChangeLayout = () => {
    // Si es Conecta GFA con tickets numerados, mostramos el logo del proveedor
    if (buckTicketEnabled && !isBulkTicket) {
      return true;
    }
    // Si es Conecta GFA con bulk tickets, usamos el layout original
    if (buckTicketEnabled && isBulkTicket) {
      return false;
    }
    // Para otros tenants, respetamos la feature flag
    return features.CHANGE_RESULT_LAYOUT;
  };

  useEffect(() => {
    if (showProviderLogosForNumberedTickets) setShowDetailsModal(false);
  }, [showProviderLogosForNumberedTickets]);

  const stopsText = t('search:trip_stopovers', {
    count: stops,
    context: stops === 0 ? 'direct' : null,
  });

  const routeCity = features.SHOW_ROUTE_LABEL
    ? getRouteLabel(path, features.ROUTE_LABEL_CITIES)
    : null;
  const routeLabelText = routeCity ? t('search:route_via', { city: routeCity }) : null;

  /**
   * Handles the selection of a trip item, showing the bus diagram if applicable.
   */
  const handleOnSelect = (funnelSection) => {
    const funnelSectionSelected = funnelSection === 'details' ? 'details' : 'results';
    onSelectClick(funnelSectionSelected);
    if (isExchange) setShowDetailsModal(false);
  };

  // TODO: Remove this when the seat on results experiment is finished
  const ctaText = isSeatsOnResults ? t('general:view_seats') : t('general:select');
  const closedText = isClosed ? getClosedTripButtonText(t, { departure, availability }) : ctaText;

  const buttonText = isOpen ? t('general:close') : closedText;
  const departureHour = moment(departure).format('LT');
  const arrivalHour = moment(arrival).format('LT');

  /**
   * Method to handle the selection of a trip
   * @param {String} funnelSection - section of the funnel where the trip was selected
   */

  const showFlatFare = useFlatFare === 'valid';
  const { brand } = environment;

  const providerAnniversary = tripHasAnniversaryIcon({
    features,
    brand,
    providerDiscount,
    departureDate: departure,
    originCode: trip.originCode,
    destinationCode: trip.destinationCode,
  });

  const { useLineNameAsServiceType = false } = environment.theme.serviceTypes || {};

  const serviceProp = serviceTypes ? service : serviceType;
  const serviceTypeLabel = useLineNameAsServiceType ? line.name : getServiceType(serviceProp).label;

  const detailsComponent = showDetailsModal ? (
    <RouteDetailsModal tripId={id} trip={trip} provider={line}>
      <div className="matrix-mobile-click">
        <FlatButton type="accent" weight="bold" size="S" mobileSize="XS">
          {t('general:details')}
        </FlatButton>
      </div>
    </RouteDetailsModal>
  ) : (
    <></>
  );
  const hidePriceOnExchange = features.HIDE_PRICE_ON_EXCHANGE && isExchange;

  const transportTypeImageSrc = environment.theme.transportTypes
    ? environment.theme.transportTypes[trip.diagramType]?.image
    : null;
  const providerLogoSrc = showProviderLogosForNumberedTickets
    ? line.logoUrl
    : transportTypeImageSrc || line.logoUrl;

  let brandToShow = features.SHOW_SERVICE_TYPE_BADGE ? serviceTypeLabel : line.name;
  if (!features.SHOW_RESULT_LINE_NAME) brandToShow = '';

  const hasProviderDiscount = hasUrgencyDiscountAmount(providerDiscount);
  const hasResultDiscountUIType = isResultDiscountUIType(pricing?.discountType);
  const urgencyDiscountUIEnabled = useUrgencyDiscountUIEnabled();
  const showDiscountUrgencyUI =
    shouldShowProviderDiscount &&
    hasProviderDiscount &&
    hasResultDiscountUIType &&
    urgencyDiscountUIEnabled;
  const showPriceWithoutDiscount =
    hasProviderDiscount && (features.SHOW_OFFICE_PRICE || showDiscountUrgencyUI);
  const hasOfficePrice = showEarlyDiscounts && officePrice && officePrice > total;
  const showTerminals = features.SHOW_TERMINALS_ON_RESULTS;
  const showDotersSecondaryPrice =
    features.DOTERS_ENABLED &&
    !features.CAN_SWITCH_WALLET_PRICE &&
    !showWalletPoints &&
    (walletPoints > 0 || showFlatFare);

  /**
   * Determine the secondary price component to display.
   * @returns {JSX.Element|null} Secondary price components or null.
   */
  const getSecondaryPrice = () => {
    if (showDotersSecondaryPrice) {
      const previousPrice =
        (showPriceWithoutDiscount && (
          <PreviousPrice price={totalBeforeDiscount} lineThrough={false} />
        )) ||
        (hasOfficePrice && <PreviousPrice price={String(officePrice)} lineThrough={false} />) ||
        null;

      return (
        <Spacing
          size="S"
          alignItems="center"
          justifyContent="flex-end"
          isResponsive
          mobileAlign="flex-end"
          responsiveSize="XS"
        >
          {previousPrice}
          <WalletPrice
            walletType="doters"
            walletTotal={walletPoints}
            size="S"
            mobileSize="S"
            iconSize="XS"
            showFlatFare={showFlatFare}
          />
        </Spacing>
      );
    }

    if (showPriceWithoutDiscount) {
      return <PreviousPrice price={totalBeforeDiscount} lineThrough={false} />;
    }
    if (hasOfficePrice) {
      return <PreviousPrice price={String(officePrice)} lineThrough={false} />;
    }
    return null;
  };
  const selectButton = highLightType ? (
    <Button
      text={buttonText}
      onClick={handleOnSelect}
      padding="L"
      size="M"
      weight="semibold"
      variant={resultsCTAColor}
      isRounded
    />
  ) : (
    <OutlineButton isRounded variant={resultsCTAColor} text={buttonText} onClick={handleOnSelect} />
  );

  const quickPurchaseButton = (
    <div className="flex gap-2 pl-2">
      <OutlineButton
        isRounded
        variant={resultsCTAColor}
        text={buttonText}
        onClick={handleOnSelect}
        padding="L"
        mobileSize="S"
      />
      {isRecommendedList ? (
        <Button
          onClick={onQuickPurchaseClick}
          padding="L"
          minHeight="41px"
          variant="quickPurchase"
          isRounded
          mobileSize="S"
        >
          <img
            src={quickPurchaseIconSrc}
            alt="Quick Purchase"
            height="auto"
            width={12}
            className="mr-1"
          />
          <span className="leading-normal">{quickPurchaseCTACopy}</span>
        </Button>
      ) : (
        <div className="group">
          <OutlineButton
            onClick={onQuickPurchaseClick}
            padding="L"
            variant="quickPurchase"
            isRounded
            mobileSize="S"
          >
            <img
              src={quickPurchaseIconSrc}
              alt="Quick Purchase"
              height="auto"
              width={12}
              className="mr-1"
            />
            <span className="bg-[linear-gradient(115deg,#B3ADFA,#BB75FF)] bg-clip-text text-transparent leading-normal group-hover:text-white">
              {quickPurchaseCTACopy}
            </span>
          </OutlineButton>
        </div>
      )}
    </div>
  );

  const comingFromLabel = comingFrom && (
    <Text size="XS" color="grayMedium">
      {`${t('search:label:comes_from')} ${comingFrom}`}
    </Text>
  );

  const shouldShowServiceType = features.SHOW_SERVICE_TYPE_BADGE && Boolean(serviceTypeLabel);
  const serviceTypeIcon =
    (serviceTypes && (serviceTypes[serviceType]?.icon || serviceTypes.default?.icon)) || 'Bus';

  /**
   * Function to determine the brand layout based on conditions
   * @returns {string} The brand layout value
   */
  const getBrandLayout = () => {
    if (isBulkTicket) return `${brand}-bulk`;
    return brand;
  };

  const tripScheduleContent = (
    <TripSchedule
      departureHour={departureHour}
      arrivalHour={arrivalHour}
      mainContent={stopsText}
      mobileSize="L"
      departureTerminal={origin.name}
      arrivalTerminal={destination.name}
      showTerminals={showTerminals}
    />
  );

  return (
    <ResultCompact
      quickPurchase={showQuickPurchase}
      brandLayout={getBrandLayout()}
      recentViewText={t('trips:recently_seen')}
      recentView={recentView}
      isOpen={isOpen}
      brand={brandToShow}
      providerName={<Text weight="bold">{brandToShow}</Text>}
      logoUrl={providerLogoSrc}
      iconDeparture={<TimeIcon time={departure} mobileSize="S" />}
      duration={
        showDuration && (
          <Text size="S" mediumSize="M" mobileSize="XS" color="grayMedium">
            {formatDuration(duration)}
          </Text>
        )
      }
      seatsRecline={
        <Text size="S" mediumSize="M" mobileSize="XS" color="grayMedium">
          {seatsRecline}
        </Text>
      }
      discount={
        <DiscountAdvice
          showDiscount={
            !hidePriceOnExchange && shouldShowProviderDiscount && !showDiscountUrgencyUI
          }
          showFlatFare={showFlatFare}
          discountType={pricing?.discountType}
          discountAvailability={pricing?.discountAvailability}
          discountAmount={providerDiscount?.amount}
          discountPercent={providerDiscount?.percent}
          way={way}
          total={total}
        />
      }
      comingFrom={comingFromLabel}
      filter={
        showNewFiltersList ? (
          <TripFiltersBadges
            filtersApplied={filtersApplied}
            trip={trip}
            minimalDesign
            renderWhenEmpty={
              <ResultCategoriesAvailability
                availableCategories={availableCategories}
                supportWoman={supportWoman}
              />
            }
          />
        ) : (
          <ResultCategoriesAvailability
            availableCategories={availableCategories}
            supportWoman={supportWoman}
          />
        )
      }
      tripType={
        <Text size="S">
          {t('search:trip_service', {
            context: variableDepartureTime ? 'on_the_way' : 'local',
          })}
        </Text>
      }
      schedule={
        routeLabelText ? (
          <div className="relative w-full">
            <Spacing fullWidth alignItems="center" justifyContent="space-between">
              {tripScheduleContent}
            </Spacing>
            <div className="absolute top-full inset-x-0 mt-0.5">
              <Text size="XS" weight="regular" color="grayLight" textAlign="center">
                {routeLabelText}
              </Text>
            </div>
          </div>
        ) : (
          tripScheduleContent
        )
      }
      details={detailsComponent}
      button={showQuickPurchase ? quickPurchaseButton : selectButton}
      price={
        !hidePriceOnExchange ? (
          <NewPriceComponent
            total={total}
            walletTotal={walletPoints}
            highLightType={highLightType}
            walletType={walletType}
            showWalletTotal={showWalletPoints}
            secondaryPrice={getSecondaryPrice()}
            lighterText={isOpen}
            showFromLabel={features.DISPLAY_FROM_PRICE_LABEL}
            tripAvailability={availability}
            lowAvailability={lowAvailability}
            providerDiscount={providerDiscount}
            discountType={pricing?.discountType}
            totalBeforeDiscount={totalBeforeDiscount}
            discountAvailability={pricing?.discountAvailability}
            discountExpiresAt={pricing?.meta?.availableUntil}
            showDiscountUrgency={showDiscountUrgencyUI}
            providerAnniversary={providerAnniversary}
          />
        ) : null
      }
      changeLayout={getChangeLayout()}
      variantShadow={features.RESULT_SHADOW_VARIANT}
      highlight={highLightType}
      mobileLayout={highLightAsMobile}
      highlightMinHeight={highLightAsMobile}
      highlightText={highlightText}
      sparkleBadgeLabel={sparkleBadgeLabel}
      serviceType={
        shouldShowServiceType && (
          <Spacing size="XS">
            <Icon type={serviceTypeIcon} size="S" />
            <Text color="primary" size="S">
              {serviceTypeLabel}
            </Text>
          </Spacing>
        )
      }
    >
      {children}
    </ResultCompact>
  );
};

const placeType = PropTypes.shape({
  name: PropTypes.string.isRequired,
  cityName: PropTypes.string.isRequired,
});

SimpleTripResult.propTypes = {
  id: PropTypes.string.isRequired,
  arrival: PropTypes.string.isRequired,
  departure: PropTypes.string.isRequired,
  destination: placeType.isRequired,
  duration: PropTypes.number.isRequired,
  onSelectClick: PropTypes.func.isRequired,
  origin: placeType.isRequired,
  providerDiscount: PropTypes.any,
  total: PropTypes.number.isRequired,
  trip: PropTypes.shape({
    line: PropTypes.shape({
      logoUrl: PropTypes.string.isRequired,
      services: PropTypes.arrayOf(PropTypes.string).isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
    pricing: PropTypes.shape({
      discountType: PropTypes.string,
      discountAvailability: PropTypes.number,
    }),
    passengerTypes: PropTypes.array,
    variableDepartureTime: PropTypes.bool,
    secondFloor: PropTypes.bool,
    stops: PropTypes.number,
    service: PropTypes.string,
    path: PropTypes.array,
    originCode: PropTypes.string,
    destinationCode: PropTypes.string,
    comingFrom: PropTypes.string,
    diagramType: PropTypes.string,
    seatsRecline: PropTypes.bool,
  }).isRequired,
  variableDepartureTime: PropTypes.bool,
  isExchange: PropTypes.bool,
  highLightType: PropTypes.bool,
  way: PropTypes.string,
  filtersApplied: PropTypes.object,
  showWalletPoints: PropTypes.string,
  walletPoints: PropTypes.number,
  useFlatFare: PropTypes.bool,
  pricing: PropTypes.shape({
    discountType: PropTypes.string,
    discountAvailability: PropTypes.number,
    meta: PropTypes.shape({
      availableUntil: PropTypes.string,
    }),
  }),
  walletType: PropTypes.string,
  serviceType: PropTypes.string.isRequired,
  highLightAsMobile: PropTypes.bool,
  availableCategories: PropTypes.array,
  supportWoman: PropTypes.bool,
  showNewFiltersList: PropTypes.bool,
  totalBeforeDiscount: PropTypes.number,
  officePrice: PropTypes.number,
  children: PropTypes.node,
  isOpen: PropTypes.bool,
  recentView: PropTypes.bool,
  highlightText: PropTypes.string,
  sparkleBadgeLabel: PropTypes.string,
  isSeatsOnResults: PropTypes.bool,
  availability: PropTypes.number,
  shouldShowProviderDiscount: PropTypes.bool,
  showQuickPurchase: PropTypes.bool,
  onQuickPurchaseClick: PropTypes.func,
  isRecommendedList: PropTypes.bool,
  isClosed: PropTypes.bool,
};

SimpleTripResult.defaultProps = {
  providerDiscount: null,
  availability: null,
  shouldShowProviderDiscount: true,
  officePrice: null,
  showQuickPurchase: false,
  onQuickPurchaseClick: () => {},
  isClosed: false,
};

DiscountAdvice.propTypes = {
  showDiscount: PropTypes.bool.isRequired,
  showFlatFare: PropTypes.bool.isRequired,
  discountType: PropTypes.string,
  discountAvailability: PropTypes.number,
  discountAmount: PropTypes.number,
  discountPercent: PropTypes.number,
  total: PropTypes.number.isRequired,
  way: PropTypes.string.isRequired,
};

export default SimpleTripResult;
